import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, CheckCircle, Clock, AlertTriangle, FileText, User, Users, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORIDADE_COLORS = {
  baixa: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  media: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
  alta: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  urgente: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
};

const STATUS_COLORS = {
  pendente: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
  em_progresso: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  concluida: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
};

const TIPO_ICONS = {
  romaneio: FileText,
  cliente: User,
  vendedor: Users
};

export const Notas = () => {
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNota, setEditingNota] = useState(null);
  const [filter, setFilter] = useState({ tipo: '', status: '', prioridade: '' });
  
  // Dados para os selects
  const [romaneios, setRomaneios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    tipo_vinculacao: 'romaneio',
    id_vinculado: '',
    status: 'pendente',
    prioridade: 'media',
    data_vencimento: '',
    tags: ''
  });

  useEffect(() => {
    fetchNotas();
    fetchVinculosData();
  }, [filter]);

  const fetchVinculosData = async () => {
    try {
      const [romRes, cliRes, venRes] = await Promise.all([
        axios.get(`${API}/romaneios`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/sellers`)
      ]);
      
      setRomaneios(romRes.data);
      setClientes(cliRes.data);
      setVendedores(venRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const fetchNotas = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.tipo) params.append('tipo', filter.tipo);
      if (filter.status) params.append('status', filter.status);
      if (filter.prioridade) params.append('prioridade', filter.prioridade);
      
      const response = await axios.get(`${API}/notas?${params}`);
      setNotas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar notas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        data_vencimento: formData.data_vencimento ? new Date(formData.data_vencimento).toISOString() : null
      };

      if (editingNota) {
        await axios.put(`${API}/notas/${editingNota.id}`, data);
        toast.success('Nota atualizada com sucesso!');
      } else {
        await axios.post(`${API}/notas`, data);
        toast.success('Nota criada com sucesso!');
      }
      
      fetchNotas();
      resetForm();
    } catch (error) {
      toast.error('Erro ao salvar nota');
      console.error('Erro:', error);
    }
  };

  const handleEdit = (nota) => {
    setEditingNota(nota);
    setFormData({
      titulo: nota.titulo,
      descricao: nota.descricao,
      tipo_vinculacao: nota.tipo_vinculacao,
      id_vinculado: nota.id_vinculado,
      status: nota.status,
      prioridade: nota.prioridade,
      data_vencimento: nota.data_vencimento ? nota.data_vencimento.split('T')[0] : '',
      tags: nota.tags ? nota.tags.join(', ') : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Tem certeza que deseja deletar esta nota?');
    if (confirmed) {
      try {
        await axios.delete(`${API}/notas/${id}`);
        toast.success('Nota deletada com sucesso!');
        fetchNotas();
      } catch (error) {
        toast.error('Erro ao deletar nota');
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingNota(null);
    setFormData({
      titulo: '',
      descricao: '',
      tipo_vinculacao: 'romaneio',
      id_vinculado: '',
      status: 'pendente',
      prioridade: 'media',
      data_vencimento: '',
      tags: ''
    });
  };

  const getVinculoOptions = () => {
    switch (formData.tipo_vinculacao) {
      case 'romaneio':
        return romaneios.map(r => ({ id: r.id, label: `#${r.numero_romaneio} - ${r.cliente_nome}` }));
      case 'cliente':
        return clientes.map(c => ({ id: c.id, label: c.nome }));
      case 'vendedor':
        return vendedores.map(v => ({ id: v.id, label: v.nome }));
      default:
        return [];
    }
  };

  const renderVinculoInfo = (nota) => {
    if (!nota.info_vinculo) return null;
    
    const Icon = TIPO_ICONS[nota.tipo_vinculacao];
    
    return (
      <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className="w-4 h-4" />
          <span className="font-medium capitalize">{nota.tipo_vinculacao}:</span>
          {nota.tipo_vinculacao === 'romaneio' && (
            <span>#{nota.info_vinculo.numero_romaneio} - {nota.info_vinculo.cliente_nome}</span>
          )}
          {nota.tipo_vinculacao === 'cliente' && (
            <span>{nota.info_vinculo.nome}</span>
          )}
          {nota.tipo_vinculacao === 'vendedor' && (
            <span>{nota.info_vinculo.nome}</span>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-heading font-bold text-foreground">Notas</h2>
          <p className="text-muted-foreground mt-1">Gerencie suas anotações e tarefas</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nova Nota
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Tipo</label>
            <select
              value={filter.tipo}
              onChange={(e) => setFilter({ ...filter, tipo: e.target.value })}
              className="input-field"
            >
              <option value="">Todos</option>
              <option value="romaneio">Romaneio</option>
              <option value="cliente">Cliente</option>
              <option value="vendedor">Vendedor</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Status</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="input-field"
            >
              <option value="">Todos</option>
              <option value="pendente">Pendente</option>
              <option value="em_progresso">Em Progresso</option>
              <option value="concluida">Concluída</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Prioridade</label>
            <select
              value={filter.prioridade}
              onChange={(e) => setFilter({ ...filter, prioridade: e.target.value })}
              className="input-field"
            >
              <option value="">Todas</option>
              <option value="baixa">Baixa</option>
              <option value="media">Média</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
        </div>
      </div>

      {/* Formulário */}
      {showForm && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-xl font-heading font-semibold mb-4">
            {editingNota ? 'Editar Nota' : 'Nova Nota'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Título *</label>
                <input
                  type="text"
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Descrição *</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="input-field min-h-[100px]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tipo de Vínculo *</label>
                <select
                  value={formData.tipo_vinculacao}
                  onChange={(e) => setFormData({ ...formData, tipo_vinculacao: e.target.value, id_vinculado: '' })}
                  className="input-field"
                  required
                >
                  <option value="romaneio">Romaneio</option>
                  <option value="cliente">Cliente</option>
                  <option value="vendedor">Vendedor</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Vincular a *</label>
                <select
                  value={formData.id_vinculado}
                  onChange={(e) => setFormData({ ...formData, id_vinculado: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="">Selecione...</option>
                  {getVinculoOptions().map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="input-field"
                >
                  <option value="pendente">Pendente</option>
                  <option value="em_progresso">Em Progresso</option>
                  <option value="concluida">Concluída</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Prioridade</label>
                <select
                  value={formData.prioridade}
                  onChange={(e) => setFormData({ ...formData, prioridade: e.target.value })}
                  className="input-field"
                >
                  <option value="baixa">Baixa</option>
                  <option value="media">Média</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Data de Vencimento</label>
                <input
                  type="date"
                  value={formData.data_vencimento}
                  onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tags (separadas por vírgula)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="Urgente, Revisar, Cobrar"
                  className="input-field"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button type="submit" className="btn-primary">
                {editingNota ? 'Atualizar' : 'Salvar'}
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Notas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {notas.length === 0 ? (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            Nenhuma nota encontrada
          </div>
        ) : (
          notas.map((nota) => (
            <div key={nota.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-lg">{nota.titulo}</h3>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(nota)} className="text-warning hover:text-warning/80">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(nota.id)} className="text-danger hover:text-danger/80">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{nota.descricao}</p>

              <div className="flex flex-wrap gap-2 mb-3">
                <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[nota.status]}`}>
                  {nota.status === 'pendente' && 'Pendente'}
                  {nota.status === 'em_progresso' && 'Em Progresso'}
                  {nota.status === 'concluida' && 'Concluída'}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${PRIORIDADE_COLORS[nota.prioridade]}`}>
                  {nota.prioridade === 'baixa' && 'Baixa'}
                  {nota.prioridade === 'media' && 'Média'}
                  {nota.prioridade === 'alta' && 'Alta'}
                  {nota.prioridade === 'urgente' && '🔥 Urgente'}
                </span>
              </div>

              {nota.tags && nota.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {nota.tags.map((tag, idx) => (
                    <span key={idx} className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {renderVinculoInfo(nota)}

              {nota.data_vencimento && (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  Vencimento: {new Date(nota.data_vencimento).toLocaleDateString('pt-BR')}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
